// Portal Gestão · Painel de Alertas (V4.52)
// - Lista alertas de compliance (view_sensitive fora do escopo esperado)
// - Resumo por usuário/motivo nas últimas 24h
// - Log detalhado com filtros
// Requer: perm permissoes.visualizar (admin/gestor)
import React, { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import { AlertTriangle, ShieldAlert, RefreshCw, Filter, User as UserIcon, Loader2 } from 'lucide-react';

const fmtDT = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('pt-BR'); } catch { return iso; }
};

export default function PortalGestaoAlertas() {
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hours, setHours] = useState(24);
  const [alertsOnly, setAlertsOnly] = useState(true);
  const [error, setError] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [sumRes, listRes] = await Promise.all([
        api.get(`/portal-gestao/audit/alerts/summary?hours=${hours}`),
        api.get(`/portal-gestao/audit?limit=200${alertsOnly ? '&alerts_only=true' : ''}`),
      ]);
      setSummary(sumRes.data);
      setItems(listRes.data?.items || []);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(typeof d === 'string' ? d : (e?.message || 'Falha ao carregar alertas'));
    } finally { setLoading(false); }
  }, [hours, alertsOnly]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto" data-testid="portal-gestao-alertas">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-wider text-rose-700 font-semibold mb-1">Compliance</div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 flex items-center gap-2">
            <ShieldAlert className="w-8 h-8 text-rose-600" /> Alertas do Portal
          </h1>
          <p className="text-sm text-slate-500 mt-1">Acessos a dados sensíveis fora do escopo esperado nas últimas <b>{hours}h</b>.</p>
        </div>
        <button
          type="button"
          onClick={fetchAll}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800 text-white text-sm font-semibold hover:bg-slate-900 disabled:opacity-50"
          data-testid="alertas-refresh"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Atualizar
        </button>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-3 mb-4 bg-white rounded-2xl border border-slate-200 p-3">
        <Filter className="w-4 h-4 text-slate-400" />
        <label className="text-xs text-slate-600 flex items-center gap-2">
          Janela:
          <select
            value={hours}
            onChange={(e) => setHours(Number(e.target.value))}
            className="border border-slate-200 rounded px-2 py-1 text-xs"
            data-testid="alertas-hours"
          >
            <option value={1}>1h</option>
            <option value={6}>6h</option>
            <option value={24}>24h</option>
            <option value={168}>7 dias</option>
            <option value={720}>30 dias</option>
          </select>
        </label>
        <label className="text-xs text-slate-600 flex items-center gap-2">
          <input
            type="checkbox"
            checked={alertsOnly}
            onChange={(e) => setAlertsOnly(e.target.checked)}
            data-testid="alertas-only-toggle"
          />
          Somente alertas
        </label>
      </div>

      {error && (
        <div className="mb-4 bg-rose-50 border border-rose-200 rounded-xl p-3 text-sm text-rose-700" data-testid="alertas-error">
          {error}
        </div>
      )}

      {/* Cards de resumo */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <div className="bg-white rounded-2xl border border-slate-200 p-4" data-testid="alertas-card-total">
          <div className="text-[10px] uppercase text-slate-500 font-semibold">Total de alertas</div>
          <div className="text-3xl font-bold text-rose-600 mt-1">{summary?.total_alerts ?? '—'}</div>
          <div className="text-[11px] text-slate-500 mt-1">Últimas {hours}h</div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-4" data-testid="alertas-card-users">
          <div className="text-[10px] uppercase text-slate-500 font-semibold">Usuários envolvidos</div>
          <div className="text-3xl font-bold text-amber-600 mt-1">{(summary?.top || []).map(r => r.actor_id).filter((v, i, a) => a.indexOf(v) === i).length}</div>
          <div className="text-[11px] text-slate-500 mt-1">Únicos</div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-4" data-testid="alertas-card-reasons">
          <div className="text-[10px] uppercase text-slate-500 font-semibold">Motivos distintos</div>
          <div className="text-3xl font-bold text-slate-800 mt-1">{(summary?.top || []).map(r => r.reason).filter((v, i, a) => a.indexOf(v) === i).length}</div>
          <div className="text-[11px] text-slate-500 mt-1">Regras acionadas</div>
        </div>
      </div>

      {/* Top ofensores */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden mb-6" data-testid="alertas-top">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600" />
          <div className="text-sm font-bold text-slate-800">Top ofensores (usuário × motivo)</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase text-slate-500 tracking-wider">
              <tr>
                <th className="text-left px-4 py-2">Usuário</th>
                <th className="text-left px-4 py-2">Motivo</th>
                <th className="text-center px-4 py-2">Ocorrências</th>
                <th className="text-left px-4 py-2">Último acesso</th>
              </tr>
            </thead>
            <tbody>
              {(summary?.top || []).length === 0 && !loading && (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-400 text-sm">
                  Nenhum alerta no período. 🎉
                </td></tr>
              )}
              {(summary?.top || []).map((r, i) => (
                <tr key={i} className="border-t border-slate-100" data-testid={`alertas-top-row-${i}`}>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <UserIcon className="w-3.5 h-3.5 text-slate-400" />
                      <div>
                        <div className="font-semibold text-slate-800">{r.actor_name || r.actor_email || '—'}</div>
                        <div className="text-[10px] text-slate-500">{r.actor_email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-2 text-slate-700">{r.reason || '—'}</td>
                  <td className="px-4 py-2 text-center">
                    <span className="inline-block px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 text-xs font-bold">{r.count}</span>
                  </td>
                  <td className="px-4 py-2 text-[12px] text-slate-500">{fmtDT(r.last_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Log detalhado */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="alertas-log">
        <div className="px-4 py-3 border-b border-slate-100">
          <div className="text-sm font-bold text-slate-800">Log detalhado</div>
          <div className="text-[11px] text-slate-500">Últimos {items.length} eventos {alertsOnly ? '(apenas alertas)' : ''}.</div>
        </div>
        <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase text-slate-500 tracking-wider sticky top-0">
              <tr>
                <th className="text-left px-3 py-2">Quando</th>
                <th className="text-left px-3 py-2">Usuário</th>
                <th className="text-left px-3 py-2">Ação</th>
                <th className="text-left px-3 py-2">Página</th>
                <th className="text-left px-3 py-2">Alvo</th>
                <th className="text-left px-3 py-2">Motivo</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && !loading && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400 text-sm">Sem eventos.</td></tr>
              )}
              {items.map((it) => (
                <tr key={it.id} className={`border-t border-slate-100 ${it.alert ? 'bg-rose-50/40' : ''}`} data-testid={`alertas-row-${it.id.slice(0, 8)}`}>
                  <td className="px-3 py-2 text-[11px] text-slate-600 whitespace-nowrap">{fmtDT(it.at)}</td>
                  <td className="px-3 py-2">
                    <div className="font-medium text-slate-800 text-[12px]">{it.actor_name || it.actor_email}</div>
                    <div className="text-[10px] text-slate-500">{it.actor_email}</div>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] uppercase font-semibold ${it.alert ? 'text-rose-700' : 'text-slate-500'}`}>{it.action}</span>
                    {it.meta?.verb && <div className="text-[10px] text-slate-500">verbo: {it.meta.verb}</div>}
                  </td>
                  <td className="px-3 py-2 text-[11px] text-slate-600 font-mono">{it.page || '—'}</td>
                  <td className="px-3 py-2 text-[11px] text-slate-600">
                    {it.target_type ? <><b>{it.target_type}</b> {it.target_id?.slice(0, 8)}</> : '—'}
                  </td>
                  <td className="px-3 py-2 text-[11px] text-rose-700">{it.alert_reason || (it.alert ? 'alerta' : '')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
